import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import {
  BanknoteIcon,
  CalendarIcon,
  CreditCardIcon,
  DollarSign,
  FileText,
  Loader2,
  Minus,
  Plus,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';
import { useCheckPaymentMethod } from '~entities/payment/api/check-payment-method';
import {
  PaymentMethod,
  useGetWalletBalance,
  useLoadWalletBalance,
  useRemoveWalletBalance,
} from '~entities/wallet';

import { ParentWalletTransactions } from '~features/students/ui/student-payment/parent-wallet-transactions';
import { useWalletBalancePolling } from '~features/students/ui/student-payment/use-wallet-balance-polling';
import { WalletExecutionDialog } from '~features/students/ui/student-payment/wallet-execution-dialog';

import { useGetCurrency } from '~shared/api/currency-api';
import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import { Textarea } from '~shared/shadcn/ui/textarea';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

interface WalletBalanceCardProps {
  parentId: string;
  studentId: string;
}

type WalletCreditPaymentMethod = 'cash' | 'check' | 'bank' | 'card';

const getTodayInputValue = () => format(new Date(), 'yyyy-MM-dd');

const parseInputDate = (value: string) => {
  const dateOnlyParts = value.split('-');
  if (dateOnlyParts.length === 3) {
    return new Date(
      Number(dateOnlyParts[0]),
      Number(dateOnlyParts[1]) - 1,
      Number(dateOnlyParts[2]),
    );
  }
  return new Date();
};

const toInputDateValue = (date: Date) => format(date, 'yyyy-MM-dd');

interface WalletPaymentDatePickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
}

const WalletPaymentDatePicker = ({
  id,
  value,
  onChange,
}: WalletPaymentDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const selectedDate = parseInputDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant='outline'
          className={cn('w-full justify-start text-left font-normal')}
        >
          <CalendarIcon className='mr-2 size-4' />
          {format(selectedDate, 'PPP')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={selectedDate}
          onSelect={(date) => {
            // react-day-picker single mode can pass undefined on deselect
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive guard for library callback
            if (!date) return;
            onChange(toInputDateValue(date));
            setOpen(false);
          }}
          required
        />
      </PopoverContent>
    </Popover>
  );
};

const walletPaymentMethodMap: Record<WalletCreditPaymentMethod, PaymentMethod> =
  {
    cash: PaymentMethod.CASH,
    check: PaymentMethod.CHECK,
    bank: PaymentMethod.US_BANK_ACCOUNT,
    card: PaymentMethod.CARD,
  };

const isChargeableWalletPaymentMethod = (
  method: WalletCreditPaymentMethod,
): boolean => method === 'card' || method === 'bank';

const toChargeCheckPaymentMethod = (
  method: WalletCreditPaymentMethod,
): 'card' | 'us_bank_account' | null => {
  if (method === 'card') return 'card';
  if (method === 'bank') return 'us_bank_account';
  return null;
};

const isChargeModeActive = (
  chargePaymentMethod: boolean,
  walletPaymentMethod: WalletCreditPaymentMethod,
): boolean =>
  chargePaymentMethod && isChargeableWalletPaymentMethod(walletPaymentMethod);

const PENDING_BANK_TRANSFER_MESSAGE =
  'A bank transfer wallet top up is already processing. You can charge another bank transfer once this payment completes.';

export const WalletBalanceCard = ({
  parentId,
  studentId,
}: WalletBalanceCardProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const { data: walletData, isLoading: isLoadingBalance } =
    useGetWalletBalance(parentId);
  const { mutateAsync: loadWalletBalance, isPending: isLoadingWallet } =
    useLoadWalletBalance(parentId);
  const { mutateAsync: removeWalletBalance, isPending: isRemovingWallet } =
    useRemoveWalletBalance(parentId);

  const [walletCreditDialogOpen, setWalletCreditDialogOpen] = useState(false);
  const [walletRemoveDialogOpen, setWalletRemoveDialogOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletPaymentMethod, setWalletPaymentMethod] =
    useState<WalletCreditPaymentMethod>('cash');
  const [walletCheckNumber, setWalletCheckNumber] = useState('');
  const [walletPaymentDate, setWalletPaymentDate] =
    useState(getTodayInputValue);
  const [walletNote, setWalletNote] = useState('');
  const [chargePaymentMethod, setChargePaymentMethod] = useState(false);
  const [walletBalancePollingTarget, setWalletBalancePollingTarget] = useState<{
    expectedBalance: number;
    startedAt: number;
  } | null>(null);
  const [removeWalletAmount, setRemoveWalletAmount] = useState('');
  const [removeWalletPaymentDate, setRemoveWalletPaymentDate] =
    useState(getTodayInputValue);
  const [removeWalletNote, setRemoveWalletNote] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [removeAmountError, setRemoveAmountError] = useState<string | null>(
    null,
  );

  const { hasAdminAccess } = useCurrentUserRole();
  const currentWalletBalance = walletData?.walletBalance ?? 0;
  const hasPendingAchWalletLoad = walletData?.hasPendingAchWalletLoad ?? false;
  const currencySymbol = currencyDetails?.symbol ?? '$';
  const chargeCheckPaymentMethod = toChargeCheckPaymentMethod(walletPaymentMethod);
  const shouldCheckSavedPaymentMethod =
    walletCreditDialogOpen && chargeCheckPaymentMethod !== null;
  const { data: savedPaymentMethodCheck, isLoading: isCheckingSavedPaymentMethod } =
    useCheckPaymentMethod({
      parentId: shouldCheckSavedPaymentMethod ? parentId : '',
      paymentMethod: chargeCheckPaymentMethod ?? 'card',
    });
  const hasSavedPaymentMethod = savedPaymentMethodCheck?.hasPaymentMethod ?? false;
  const isPendingBankChargeBlocked =
    walletPaymentMethod === 'bank' && hasPendingAchWalletLoad;
  const isChargeCheckboxDisabled =
    !isChargeableWalletPaymentMethod(walletPaymentMethod) ||
    isCheckingSavedPaymentMethod ||
    !hasSavedPaymentMethod ||
    isPendingBankChargeBlocked;
  const isCharging = isChargeModeActive(
    chargePaymentMethod,
    walletPaymentMethod,
  );

  const handleWalletBalancePollingComplete = useCallback(() => {
    setWalletBalancePollingTarget(null);
  }, []);

  useWalletBalancePolling(
    parentId,
    walletBalancePollingTarget,
    handleWalletBalancePollingComplete,
  );

  // Reset wallet credit form when dialog opens/closes
  useEffect(() => {
    if (walletCreditDialogOpen) {
      setWalletAmount('');
      setWalletPaymentMethod('cash');
      setWalletCheckNumber('');
      setWalletPaymentDate(getTodayInputValue());
      setWalletNote('');
      setChargePaymentMethod(false);
      setAmountError(null);
    }
  }, [walletCreditDialogOpen]);

  useEffect(() => {
    if (
      isChargeableWalletPaymentMethod(walletPaymentMethod) &&
      !isCheckingSavedPaymentMethod &&
      !hasSavedPaymentMethod &&
      chargePaymentMethod
    ) {
      setChargePaymentMethod(false);
    }
  }, [
    walletPaymentMethod,
    isCheckingSavedPaymentMethod,
    hasSavedPaymentMethod,
    chargePaymentMethod,
  ]);

  useEffect(() => {
    if (isPendingBankChargeBlocked && chargePaymentMethod) {
      setChargePaymentMethod(false);
    }
  }, [isPendingBankChargeBlocked, chargePaymentMethod]);

  useEffect(() => {
    if (walletRemoveDialogOpen) {
      setRemoveWalletAmount('');
      setRemoveWalletPaymentDate(getTodayInputValue());
      setRemoveWalletNote('');
      setRemoveAmountError(null);
    }
  }, [walletRemoveDialogOpen]);

  const validateAmount = (value: string): boolean => {
    // Check if the value matches the pattern: digits followed by optional decimal point and up to 2 decimal digits
    const regex = /^\d+(\.\d{0,2})?$/;
    return regex.test(value);
  };

  const handleWalletAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWalletAmount(value);

    if (value === '' || validateAmount(value)) {
      setAmountError(null);
    } else {
      setAmountError('Please enter a valid amount with up to 2 decimal places');
    }
  };

  const handleRemoveWalletAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setRemoveWalletAmount(value);

    if (value === '' || validateAmount(value)) {
      setRemoveAmountError(null);
    } else {
      setRemoveAmountError(
        'Please enter a valid amount with up to 2 decimal places',
      );
    }
  };

  const handleAddCreditToWallet = async () => {
    const amount = Number.parseFloat(walletAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setAmountError('Please enter a valid amount greater than 0');
      return;
    }

    if (walletPaymentMethod === 'check' && !walletCheckNumber.trim()) {
      toast.error('Please enter a check number.');
      return;
    }

    if (!walletPaymentDate) {
      toast.error('Please select a payment date.');
      return;
    }

    const isChargingRequest = isChargeModeActive(
      chargePaymentMethod,
      walletPaymentMethod,
    );

    if (isChargingRequest && isPendingBankChargeBlocked) {
      toast.warning(PENDING_BANK_TRANSFER_MESSAGE);
      return;
    }

    try {
      const response = await loadWalletBalance({
        amount,
        paymentMethod: walletPaymentMethodMap[walletPaymentMethod],
        paymentDate: walletPaymentDate,
        ...(walletPaymentMethod === 'check'
          ? { checkNumber: walletCheckNumber.trim() }
          : {}),
        ...(walletNote.trim() ? { note: walletNote.trim() } : {}),
        ...(isChargeableWalletPaymentMethod(walletPaymentMethod) &&
        chargePaymentMethod
          ? { chargePaymentMethod: true }
          : {}),
      });

      if (response.status === 'pending') {
        toast.warning(response.message ?? PENDING_BANK_TRANSFER_MESSAGE);
        setWalletCreditDialogOpen(false);
        return;
      }

      if (isChargingRequest && response.hasPaymentMethod === false) {
        toast.error(
          'Parent does not have a saved payment method on file for this type.',
        );
        return;
      }

      if (isChargingRequest) {
        setWalletBalancePollingTarget({
          expectedBalance: currentWalletBalance + amount,
          startedAt: Date.now(),
        });
        toast.success(
          response.message ??
            `${currencyDetails?.symbol ?? '$'}${amount} charge initiated. Wallet balance will update once payment completes.`,
        );
      } else {
        toast.success(
          `${currencyDetails?.symbol ?? '$'}${amount} has been added to the wallet.`,
        );
      }
      setWalletCreditDialogOpen(false);
    } catch (error) {
      console.error('Error adding credit to wallet:', error);
      toast.error('Failed to add credit to wallet.');
    }
  };

  const handleRemoveCreditFromWallet = async () => {
    const amount = Number.parseFloat(removeWalletAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setRemoveAmountError('Please enter a valid amount greater than 0');
      return;
    }

    if (amount > currentWalletBalance) {
      setRemoveAmountError(
        `Amount cannot exceed the current wallet balance of ${currencyDetails?.symbol ?? '$'}${currentWalletBalance.toFixed(2)}`,
      );
      return;
    }

    try {
      await removeWalletBalance({
        amount,
        paymentDate: removeWalletPaymentDate,
        ...(removeWalletNote.trim() ? { note: removeWalletNote.trim() } : {}),
      });

      toast.success(
        `${currencyDetails?.symbol ?? '$'}${amount} has been removed from the wallet.`,
      );
      setWalletRemoveDialogOpen(false);
    } catch (error) {
      console.error('Error removing credit from wallet:', error);
      if (isAxiosError(error)) {
        const apiMessage = error.response?.data?.message;
        if (error.response?.status === 403) {
          toast.error(
            typeof apiMessage === 'string'
              ? apiMessage
              : 'Admin access required to remove wallet credit.',
          );
          return;
        }
        if (typeof apiMessage === 'string' && apiMessage.trim()) {
          toast.error(apiMessage);
          return;
        }
      }
      toast.error('Failed to remove credit from wallet.');
    }
  };

  const showRecordOnlyNotice = !isCharging;

  if (isLoadingBalance) {
    return (
      <Card className='flex flex-col'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm font-medium'>Wallet Balance</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-1 flex-col justify-center py-2'>
          <div className='animate-pulse text-3xl font-bold'>Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className='flex flex-row items-center gap-2'>
        <CardTitle className='flex items-center gap-2'>
          Wallet Balance
          {hasPendingAchWalletLoad && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex text-amber-600'>
                  <Loader2 className='size-4 animate-spin' />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Bank transfer wallet load in progress
              </TooltipContent>
            </Tooltip>
          )}
        </CardTitle>
        <ParentWalletTransactions parentId={parentId} />
      </CardHeader>
      <CardContent className='flex flex-col gap-2'>
        <div className='flex flex-row items-center justify-between'>
        <div className='text-3xl font-bold'>
          {currencyDetails?.symbol ?? '$'}
          {(walletData?.walletBalance ?? 0).toFixed(2)}
        </div>
        {hasAdminAccess && (
          <div className='mt-2 flex flex-wrap items-center gap-1'>
            <Dialog
              open={walletCreditDialogOpen}
              onOpenChange={setWalletCreditDialogOpen}
            >
              <DialogTrigger asChild>
                <Button size='sm' variant='outline' className='h-7 gap-1'>
                  <Plus className='size-3.5' />
                  <span>Add Credit</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Credit to Wallet</DialogTitle>
                  <DialogDescription>
                    Add funds to the parent&apos;s wallet balance.
                  </DialogDescription>
                </DialogHeader>

                <>
                  {isCharging && (
                    <p className='rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive'>
                      This will charge the user&apos;s saved payment method now
                      and add the amount to their wallet balance.
                    </p>
                  )}

                  {showRecordOnlyNotice && (
                    <p className='rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground'>
                      This amount will only be recorded and no payment will be
                      made against this.
                    </p>
                  )}

                  {isPendingBankChargeBlocked && (
                    <p className='rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700'>
                      {PENDING_BANK_TRANSFER_MESSAGE}
                    </p>
                  )}

                  <div className='grid gap-4 py-4'>
                    <div className='grid gap-2'>
                      <Label htmlFor='wallet-amount'>Amount</Label>
                      <div className='relative'>
                        <DollarSign className='absolute left-2.5 top-2.5 size-4 text-muted-foreground' />
                        <Input
                          id='wallet-amount'
                          placeholder='Enter amount'
                          type='number'
                          min='0'
                          step='0.01'
                          className='pl-8'
                          value={walletAmount}
                          onChange={handleWalletAmountChange}
                          onKeyDown={(e) => {
                            // Prevent 'e', 'E', '-', and '+' characters in number input
                            if (
                              e.key === 'e' ||
                              e.key === 'E' ||
                              e.key === '-' ||
                              e.key === '+'
                            ) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                      {amountError && (
                        <p className='text-sm text-red-500'>{amountError}</p>
                      )}
                    </div>
                    <div className='grid gap-2'>
                      <Label>Payment Method</Label>
                      <RadioGroup
                        value={walletPaymentMethod}
                        onValueChange={(value) => {
                          const method = value as WalletCreditPaymentMethod;
                          setWalletPaymentMethod(method);
                          if (!isChargeableWalletPaymentMethod(method)) {
                            setChargePaymentMethod(false);
                          }
                        }}
                        className='grid gap-2'
                      >
                        <div className='flex items-center space-x-2 rounded-md border p-2'>
                          <RadioGroupItem value='cash' id='wallet-cash' />
                          <Label
                            htmlFor='wallet-cash'
                            className='flex cursor-pointer items-center'
                          >
                            <BanknoteIcon className='mr-2 size-4' />
                            Cash
                          </Label>
                        </div>
                        <div className='flex items-center space-x-2 rounded-md border p-2'>
                          <RadioGroupItem value='check' id='wallet-check' />
                          <Label
                            htmlFor='wallet-check'
                            className='flex cursor-pointer items-center'
                          >
                            <FileText className='mr-2 size-4' />
                            Check
                          </Label>
                        </div>
                        <div className='flex items-center space-x-2 rounded-md border p-2'>
                          <RadioGroupItem value='bank' id='wallet-bank' />
                          <Label
                            htmlFor='wallet-bank'
                            className='flex cursor-pointer items-center'
                          >
                            <Wallet className='mr-2 size-4' />
                            Bank Transfer
                          </Label>
                        </div>
                        <div className='flex items-center space-x-2 rounded-md border p-2'>
                          <RadioGroupItem value='card' id='wallet-card' />
                          <Label
                            htmlFor='wallet-card'
                            className='flex cursor-pointer items-center'
                          >
                            <CreditCardIcon className='mr-2 size-4' />
                            Card
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div
                      className={cn(
                        'flex items-start gap-2 rounded-md border p-3',
                        isCharging
                          ? 'border-destructive bg-destructive/10'
                          : 'border-destructive/30 bg-destructive/5',
                      )}
                    >
                      <Checkbox
                        id='wallet-charge-payment-method'
                        checked={isCharging}
                        disabled={isChargeCheckboxDisabled}
                        className={cn(
                          isCharging &&
                            'border-destructive data-[state=checked]:bg-destructive data-[state=checked]:text-destructive-foreground',
                        )}
                        onCheckedChange={(checked) => {
                          if (isChargeCheckboxDisabled) {
                            return;
                          }
                          setChargePaymentMethod(checked === true);
                        }}
                      />
                      <div className='grid gap-1'>
                        <Label
                          htmlFor='wallet-charge-payment-method'
                          className={cn(
                            'font-medium leading-snug',
                            isChargeCheckboxDisabled
                              ? 'cursor-not-allowed text-muted-foreground'
                              : 'cursor-pointer text-destructive',
                          )}
                        >
                          Charge parent&apos;s saved payment method
                        </Label>
                        {!isChargeableWalletPaymentMethod(walletPaymentMethod) && (
                          <p className='text-sm text-muted-foreground'>
                            Available only for Card or Bank Transfer.
                          </p>
                        )}
                        {isChargeableWalletPaymentMethod(walletPaymentMethod) &&
                          isPendingBankChargeBlocked && (
                            <p className='text-sm text-amber-700'>
                              {PENDING_BANK_TRANSFER_MESSAGE}
                            </p>
                          )}
                        {isChargeableWalletPaymentMethod(walletPaymentMethod) &&
                          !isPendingBankChargeBlocked &&
                          isCheckingSavedPaymentMethod && (
                            <p className='animate-pulse text-sm text-muted-foreground'>
                              Checking saved payment methods...
                            </p>
                          )}
                        {isChargeableWalletPaymentMethod(walletPaymentMethod) &&
                          !isPendingBankChargeBlocked &&
                          !isCheckingSavedPaymentMethod &&
                          !hasSavedPaymentMethod && (
                            <p className='text-sm text-destructive'>
                              No saved payment method found for this parent.
                            </p>
                          )}
                      </div>
                    </div>

                    {walletPaymentMethod === 'check' && (
                      <div className='grid gap-2'>
                        <Label htmlFor='wallet-check-number'>
                          Check Number
                        </Label>
                        <Input
                          id='wallet-check-number'
                          placeholder='Enter check number'
                          value={walletCheckNumber}
                          onChange={(e) => {
                            setWalletCheckNumber(e.target.value);
                          }}
                        />
                      </div>
                    )}

                    <div className='grid gap-2'>
                      <Label htmlFor='wallet-payment-date'>Payment Date</Label>
                      <WalletPaymentDatePicker
                        id='wallet-payment-date'
                        value={walletPaymentDate}
                        onChange={setWalletPaymentDate}
                      />
                    </div>

                    <div className='grid gap-2'>
                      <Label htmlFor='wallet-note'>Note (optional)</Label>
                      <Textarea
                        id='wallet-note'
                        placeholder='Add a note about this payment'
                        value={walletNote}
                        onChange={(e) => {
                          setWalletNote(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant='outline'
                      onClick={() => {
                        setWalletCreditDialogOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddCreditToWallet}
                      disabled={
                        isLoadingWallet ||
                        !!amountError ||
                        !walletPaymentDate ||
                        (walletPaymentMethod === 'check' &&
                          !walletCheckNumber.trim()) ||
                        (isCharging && isPendingBankChargeBlocked)
                      }
                    >
                      {isLoadingWallet
                        ? isCharging
                          ? 'Charging...'
                          : 'Adding...'
                        : isCharging
                          ? 'Charge & Add Credit'
                          : 'Add Credit'}
                    </Button>
                  </DialogFooter>
                </>
              </DialogContent>
            </Dialog>
            <Dialog
              open={walletRemoveDialogOpen}
              onOpenChange={setWalletRemoveDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  size='sm'
                  variant='outline'
                  className='h-7 gap-1'
                  disabled={currentWalletBalance <= 0}
                >
                  <Minus className='size-3.5' />
                  <span>Remove Credit</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove Credit from Wallet</DialogTitle>
                  <DialogDescription>
                    This only adjusts the wallet ledger. No refund will be
                    issued to the parent&apos;s card.
                  </DialogDescription>
                </DialogHeader>

                <div className='grid gap-4 py-4'>
                  <div className='grid gap-2'>
                    <Label htmlFor='remove-wallet-amount'>Amount</Label>
                    <div className='relative'>
                      <DollarSign className='absolute left-2.5 top-2.5 size-4 text-muted-foreground' />
                      <Input
                        id='remove-wallet-amount'
                        placeholder='Enter amount'
                        type='number'
                        min='0'
                        max={currentWalletBalance}
                        step='0.01'
                        className='pl-8'
                        value={removeWalletAmount}
                        onChange={handleRemoveWalletAmountChange}
                        onKeyDown={(e) => {
                          if (
                            e.key === 'e' ||
                            e.key === 'E' ||
                            e.key === '-' ||
                            e.key === '+'
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      Available balance: {currencyDetails?.symbol ?? '$'}
                      {currentWalletBalance.toFixed(2)}
                    </p>
                    {removeAmountError && (
                      <p className='text-sm text-red-500'>
                        {removeAmountError}
                      </p>
                    )}
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='remove-wallet-payment-date'>
                      Payment Date
                    </Label>
                    <WalletPaymentDatePicker
                      id='remove-wallet-payment-date'
                      value={removeWalletPaymentDate}
                      onChange={setRemoveWalletPaymentDate}
                    />
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='remove-wallet-note'>Note (optional)</Label>
                    <Textarea
                      id='remove-wallet-note'
                      placeholder='Add a note about this adjustment'
                      value={removeWalletNote}
                      onChange={(e) => {
                        setRemoveWalletNote(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setWalletRemoveDialogOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='destructive'
                    onClick={handleRemoveCreditFromWallet}
                    disabled={
                      isRemovingWallet ||
                      !!removeAmountError ||
                      !removeWalletPaymentDate
                    }
                  >
                    {isRemovingWallet ? 'Removing...' : 'Remove Credit'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <WalletExecutionDialog
              parentId={parentId}
              studentId={studentId}
              currentWalletBalance={currentWalletBalance}
              currencySymbol={currencySymbol}
            />
          </div>
        )}
        </div>
        {hasPendingAchWalletLoad && (
          <p className='text-sm text-amber-700'>
            A bank transfer wallet top up is processing. The balance will update
            automatically once payment completes.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
